import { useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { Dot, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';

import { useFormatTimeIndicator } from '@/hooks/format/useFormatTimeIndicator';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

import Icon from '@/components/base/Icon';
import TooltipCustom from '@/components/composite/Charts/components/TooltipCustom';

// Components
import ChartWrapper from './ChartWrapper';

const CustomShape = ({ x, y, r = 3, color, highlight, dataConfig, isExport, ...restProps }) => {
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';

    if (!dataConfig?.colors?.[theme]) {
        console.warn('HeatMapChart CustomShape: invalid dataConfig', { dataConfig, theme, color });
        return null;
    }

    const isBestTimeToPost = [
        'best_time_to_post',
        'stories_best_time_to_post',
        'fans_online',
        'posting_habits'
    ].includes(dataConfig.id);

    return (
        <>
            <Dot cy={y} cx={x} r={r} fill={dataConfig.colors[theme][color]} {...restProps} />
            {highlight && (
                <foreignObject x={x - 1.5} y={y - 6} width={r * 2} height={r * 2}>
                    <Icon
                        icon={dataConfig?.iconHighestValue}
                        className="text-xs"
                        style={{
                            color:
                                isBestTimeToPost && theme === 'light'
                                    ? 'white'
                                    : darkModeColorsChart.dark[dataConfig.color?.value]
                        }}
                    />
                </foreignObject>
            )}
        </>
    );
};

CustomShape.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    r: PropTypes.number,
    color: PropTypes.string,
    highlight: PropTypes.bool,
    dataConfig: PropTypes.object,
    isExport: PropTypes.bool
};

const HeatMapChartComponent = ({ data, activeFilter, isExport, height = 300 }) => {
    const { t } = useTranslation('analytics');
    const { formatWeekDays, formatHours, formatHour } = useFormatTimeIndicator();

    const dataConfig = useMemo(() => {
        if (!data) {
            console.log('HeatMapChart: data is null/undefined');
            return null;
        }
        if (!data.graphData) {
            console.warn('HeatMapChart: data.graphData is missing', data);
            return null;
        }
        // Only recalculate if the data really changes
        return data;
    }, [data?.graphData, data?.min, data?.max, data?.id]);

    const valueGroups = useMemo(() => {
        // Protection: check that the data exists
        if (!dataConfig?.graphData?.length) {
            console.warn('HeatMapChart: no graph data available');
            return [];
        }

        if (!dataConfig.breakpoints || dataConfig.breakpoints.length === 0) {
            console.warn('HeatMapChart: no breakpoints defined', dataConfig);
            return [];
        }

        const average = dataConfig.max - dataConfig.min;

        const finalData = dataConfig.graphData.map((item) => ({
            ...item,
            date: formatHour(item.date)
        }));

        let firstBreakpoint = undefined;
        // Create a deep copy of breakpoints to avoid modifying read-only objects
        let breakpoints = dataConfig.breakpoints.map((breakpoint) => ({ ...breakpoint }));

        const minGraphValue = Math.min(...dataConfig.graphData.map((item) => item.value));

        if (minGraphValue === 0) {
            // Extract the first breakpoint
            firstBreakpoint = breakpoints.shift();
            // Create a new copy with the modified value
            firstBreakpoint = { ...firstBreakpoint, value: 0 };
        }

        const finalBreakpoints = breakpoints.map((item, index) => ({
            ...item,
            value: dataConfig.min + (index + 1) * (average / breakpoints.length) // Get breakpoints values depending on account's followers
        }));

        if (firstBreakpoint) {
            finalBreakpoints.unshift(firstBreakpoint);
        }

        const heatGroups = finalBreakpoints.map(({ value, color, r }, idx) => {
            return {
                color,
                data: finalData.filter((d) => {
                    return idx === 0
                        ? d.value <= value
                        : d.value <= value && d.value > finalBreakpoints[idx - 1].value;
                }),
                r
            };
        });

        const remaining = finalData.filter(
            ({ value: value1 }) => finalBreakpoints[finalBreakpoints.length - 1].value < value1
        );

        if (remaining.length > 0) {
            heatGroups.push({
                color: finalBreakpoints[finalBreakpoints.length - 1].color,
                data: remaining,
                r: 12.5
            });
        }

        return heatGroups;
    }, [dataConfig, formatHour]);

    if (!dataConfig?.graphData?.length) {
        return null;
    }

    return (
        <div className="relative h-full w-full overflow-x-auto" style={{ minHeight: height }}>
            <div className="absolute left-0 top-0 h-full w-full">
                <ResponsiveContainer
                    height={height}
                    width={isExport ? data.fixedSize : '100%'}
                    minWidth={dataConfig.minWidth}>
                    <ScatterChart margin={dataConfig.margin}>
                        <XAxis
                            allowDuplicatedCategory={false}
                            type="category"
                            dataKey="date"
                            domain={formatHours()}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={10}
                            minTickGap={-10}
                        />
                        <YAxis
                            allowDuplicatedCategory={false}
                            type="category"
                            dataKey="day"
                            domain={formatWeekDays()}
                            reversed
                            tickFormatter={(day) => t(`commons:days.${day}`).substring(0, 3)}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            cursor={false}
                            content={
                                <TooltipCustom
                                    valueFormat={data.valueFormat}
                                    tooltipTranslation={data.tooltipTranslation}
                                    data={data}
                                    activeFilter={activeFilter}
                                />
                            }
                        />

                        {valueGroups.map(({ data, color, r }, index) => {
                            return (
                                <Scatter
                                    key={index}
                                    r={r}
                                    data={data}
                                    color={color}
                                    shape={
                                        <CustomShape dataConfig={dataConfig} isExport={isExport} />
                                    }
                                />
                            );
                        })}
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

HeatMapChartComponent.propTypes = {
    data: PropTypes.object,
    activeFilter: PropTypes.string,
    height: PropTypes.number
};

const HeatMapChart = ({
    data,
    onReset = () => {
        /** */
    },
    isLoading = false,
    className = '',
    ...restProps
}) => {
    // Protection against missing data when navigating
    if (!data) {
        console.warn('HeatMapChart: data prop is missing');
        return null;
    }

    return (
        <ChartWrapper
            className={className}
            isLoading={isLoading}
            onReset={onReset}
            data={data}
            legendType={data.legendType ?? 'heatMap'}
            {...restProps}>
            <HeatMapChartComponent />
        </ChartWrapper>
    );
};

export { HeatMapChart, HeatMapChartComponent };
